import {
  Box,
  Typography,
  Divider,
  Paper,
  Chip,
  Card,
  CardContent,
  useTheme,
  Alert,
  AlertTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Link as MuiLink,
} from "@mui/material";
import { Editor } from "@monaco-editor/react";
import TimelineIcon from "@mui/icons-material/Timeline";
import PieChartIcon from "@mui/icons-material/PieChart";
import CodeIcon from "@mui/icons-material/Code";
import StorageIcon from "@mui/icons-material/Storage";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CssIcon from "@mui/icons-material/Style";

// --- Components Helpers ---

const MainTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h5"
    sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}
  >
    {children}
  </Typography>
);

const SectionHeader = ({
  children,
  number,
  icon,
}: {
  children: React.ReactNode;
  number: string;
  icon?: React.ReactNode;
}) => (
  <Box sx={{ mt: 5, mb: 3 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}
    >
      <Chip
        label={number}
        color="primary"
        size="small"
        sx={{ fontWeight: "bold", borderRadius: 1, color: "white" }}
      />
      {children}
      {icon && <Box sx={{ ml: "auto", color: "text.disabled" }}>{icon}</Box>}
    </Typography>
    <Divider sx={{ mt: 1 }} />
  </Box>
);

const SubSectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="subtitle1"
    sx={{
      mt: 3,
      mb: 1.5,
      fontWeight: "bold",
      color: "text.primary",
      borderLeft: 3,
      borderColor: "primary.main",
      pl: 1.5,
    }}
  >
    {children}
  </Typography>
);

const PropertyList = ({
  items,
}: {
  items: { name: string; desc: string; required?: boolean; example?: string }[];
}) => {
  return (
    <Box sx={{ mb: 2, borderRadius: 1, p: 0.5 }}>
      {items.map((row) => (
        <Box
          key={row.name}
          sx={{
            py: 1,
            px: 1,
            borderBottom: "1px solid",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container spacing={1} alignItems="flex-start">
            {/* Nome da Propriedade - Full width na mobile, 3 cols no desktop */}
            <Grid item xs={12} sm={3}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: "bold",
                  color: "primary.main",
                  fontSize: "0.85rem",
                  display: "block",
                }}
              >
                {row.name}
              </Typography>
              {row.required && (
                <Typography
                  variant="caption"
                  color="secondary"
                  sx={{
                    display: "block", // Block para ficar embaixo do nome
                    fontSize: "0.65rem",
                    fontWeight: "bold",
                    mt: 0.5,
                  }}
                >
                  *Obrigatório
                </Typography>
              )}
            </Grid>

            {/* Descrição e Exemplo - Full width na mobile, 9 cols no desktop */}
            <Grid item xs={12} sm={9}>
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                {row.desc}
              </Typography>

              {row.example && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: "inline-block",
                    mt: 0.5,
                    fontFamily: "monospace",
                    fontSize: "0.7rem",
                    bgcolor: "action.hover",
                    color: "text.primary",
                    px: 0.8,
                    py: 0.3,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    maxWidth: "100%",
                    overflowX: "auto",
                  }}
                >
                  <b>Ex:</b> {row.example}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

const FunctionCard = ({
  name,
  desc,
  params,
  exampleData,
}: {
  name: string;
  desc: string;
  params: string[];
  exampleData?: string;
}) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.03)"
            : "#f8f9fa",
      }}
    >
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "monospace",
              fontSize: "1rem",
              fontWeight: "bold",
              color: "primary.main",
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.primary" }}
          >
            - {desc}
          </Typography>
        </Box>

        <Box sx={{ pl: 0, mb: 1 }}>
          {params.map((p, i) => (
            <Typography
              key={i}
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
            >
              • {p}
            </Typography>
          ))}
        </Box>

        {exampleData && (
          <Box
            sx={{
              bgcolor:
                theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "#fff",
              p: 1,
              borderRadius: 1,
              border: `1px dashed ${theme.palette.divider}`,
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: "bold",
                display: "block",
                mb: 0.5,
              }}
            >
              Exemplo de entrada:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
            >
              {exampleData}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CodeSnippet = ({
  code,
  language = "javascript",
  height = "150px",
}: {
  code: string;
  language?: string;
  height?: string;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: "hidden",
        my: 2,
        boxShadow: 1,
      }}
    >
      <Editor
        height={height}
        defaultLanguage={language}
        value={code}
        theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          lineNumbers: "off",
          scrollBeyondLastLine: false,
          folding: false,
          overviewRulerLanes: 0,
          renderLineHighlight: "none",
          contextmenu: false,
          fontFamily: "monospace",
          fontSize: 12,
        }}
      />
    </Box>
  );
};

export const HelpComponent = () => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 4, maxWidth: "100%", color: "text.primary" }}>
      {/* HEADER */}
      <MainTitle>GUIA DE CRIAÇÃO DE FUNÇÕES</MainTitle>
      <Typography variant="body1" paragraph color="text.secondary">
        Ferramenta para criação de componentes reutilizáveis em relatórios PDF.
      </Typography>
      <Alert severity="info" variant="outlined" sx={{ mb: 4 }}>
        <AlertTitle>Fluxo de Trabalho</AlertTitle>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <Chip
            size="small"
            label="1. Definir Variáveis"
            color="secondary"
            variant="outlined"
          />
          <Typography variant="body2">→</Typography>
          <Chip
            size="small"
            label="2. Processar Dados (JS)"
            color="secondary"
            variant="outlined"
          />
          <Typography variant="body2">→</Typography>
          <Chip
            size="small"
            label="3. Estruturar Visual (PDF)"
            color="secondary"
            variant="outlined"
          />
        </Box>
      </Alert>
      {/* 1. VARIÁVEIS */}
      <SectionHeader number="1" icon={<StorageIcon />}>
        Variáveis (Formulário)
      </SectionHeader>
      <Typography variant="body2">
        Defina as entradas de dados (items do Zabbix). No campo de variável,
        digite apenas o nome{" "}
        <Chip
          label="nomeDaVariavel"
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: "bold",
            verticalAlign: "middle",
          }}
        />
        . Para usar no código, utilize com underscores:{" "}
        <Chip
          label="_nomeDaVariavel_"
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: "bold",
            verticalAlign: "middle",
          }}
        />
        .
      </Typography>

      <Card
        variant="outlined"
        sx={{ mt: 2, bgcolor: theme.palette.action.hover }}
      >
        <CardContent sx={{ py: 2 }}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Estrutura dos Dados
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            Ao utilizar a variável no código, ela será substituída por um objeto
            com a seguinte estrutura:
          </Typography>

          <Alert
            severity="warning"
            sx={{ "& .MuiAlert-icon": { py: 0, alignSelf: "center" } }}
          >
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
              Atenção: Se a opção "Múltiplos" estiver marcada, a variável será
              um <code>Array</code> contendo um objeto deste para cada host
              selecionado.
            </Typography>
          </Alert>

          <CodeSnippet
            language="json"
            height="260px"
            code={`{
  "itemName": ["GigabitEthernet0/1"],
  "hostName": ["SW-MW-Core-01 - 192.168.1.1"],
  "dataInicial": "2026-01-26T16:00:00.000Z",
  "dataFinal": "2026-02-26T16:00:00.000Z",
  "data": [
    {
      "itemId": "1234567",
      "itemName": "GigabitEthernet0/1",
      "hostid": "54321",
      "hostName": "SW-MW-Core-01",
      "clock": 1772118568,
      "value": 49522777
    }, ...
  ]
}`}
          />
        </CardContent>
      </Card>
      {/* 2. CÓDIGO */}
      <SectionHeader number="2" icon={<CodeIcon />}>
        Código (JavaScript)
      </SectionHeader>
      <Typography variant="body2">
        Processe os dados brutos aqui. O código deve retornar um objeto JSON.
      </Typography>
      <SubSectionTitle>Variáveis Globais</SubSectionTitle>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mb: 3,
          bgcolor: theme.palette.mode === "dark" ? "transparent" : "#f8f9fa",
        }}
      >
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell width={140}>
                <Chip
                  label="_titulo_"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  sx={{ fontFamily: "monospace" }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  Título digitado pelo usuário na geração do relatório.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Chip
                  label="_dataInicial_"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  sx={{ fontFamily: "monospace" }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  Data de início no formato <b>DD-MM-YYYY</b>.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Chip
                  label="_dataFinal_"
                  color="secondary"
                  variant="outlined"
                  size="small"
                  sx={{ fontFamily: "monospace" }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  Data de fim no formato <b>DD-MM-YYYY</b>.
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <SubSectionTitle>Funções Utilitárias Disponíveis</SubSectionTitle>
      <FunctionCard
        name="groupByInterval(data, intervalInMinutes, fator)"
        desc="Agrupa séries temporais por intervalo de tempo fazendo média."
        params={[
          "data: Array<{ clock: number, value: number }>",
          "intervalInMinutes: number",
          "fator: number (opcional, padrão 1)",
        ]}
        exampleData={`input: [{clock: 100, value: 10}, {clock: 100, value: 20}, {clock: 200, value: 20}] \noutput: [{clock: 100, value: 15}, {clock: 200, value: 20}] (Média)`}
      />
      <FunctionCard
        name="groupByIntervalTimeline(data, dataInicial, dataFinal)"
        desc="Gera uma timeline rigorosa com base no delay do item Zabbix, preenchendo lacunas (quedas de rede) com null."
        params={[
          "data: Array<{ clock: number, value: number, delay?: string }>",
          "dataInicial: string (YYYY-MM-DD)",
          "dataFinal: string (YYYY-MM-DD)",
        ]}
        exampleData={`Gera pontos uniformemente espaçados no período. Quando há queda de rede (sem dados), o valor fica null, desenhando lacunas no gráfico.`}
      />
      <FunctionCard
        name="calculate_95th(data)"
        desc="Calcula percentil 95."
        params={["data: Array<{ value: number }>"]}
        exampleData={`input: [{value: 10}, {value: 100}, {value: 12}] \noutput: 12`}
      />
      <FunctionCard
        name="convertBpsUnit(valor)"
        desc="Converte bits para Kbps/Mbps/Gbps automaticamente."
        params={["valor: string | number"]}
        exampleData={`input: 1000000 \noutput: "1 Mbps"`}
      />
      <FunctionCard
        name="dayjs"
        desc="Manipulação avançada de datas."
        params={['Exemplo: dayjs().format("DD/MM/YYYY")']}
      />
      <SubSectionTitle>
        Exemplo Prático: Preparando dados para Gráfico
      </SubSectionTitle>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <AlertTitle>Dica de Depuração</AlertTitle>
        <Typography variant="body2">
          Para visualizar o conteúdo de uma variável durante o desenvolvimento,
          basta retorná-la diretamente.
          <br />
          <b>Exemplo:</b> <code>return dados</code> ou{" "}
          <code>return raw.data;</code>
        </Typography>
      </Alert>
      <CodeSnippet
        height="230px"
        code={`const raw = _itemZabbix_;
const dados = groupByIntervalTimeline(raw.data, dataInicial, dataFinal);

// 1. Array de strings para o Eixo X
const labels = dados.map(d => d.clock);

// 2. Array de valores para o Eixo Y
const values = dados.map(d => d.value);

// Retornar objeto com os dados processados
return {
    graficoDados: values,
    graficoLabels: labels,
};`}
      />
      {/* 3. VIEW */}
      <SectionHeader number="3" icon={<PictureAsPdfIcon />}>
        View (Layout PDF)
      </SectionHeader>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: theme.palette.action.selected,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          "Embora o retorno final deva ser a estrutura JSON do{" "}
          <MuiLink
            href="https://pdfmake.github.io/docs/0.1/"
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            color="primary"
            fontWeight="bold"
          >
            pdfMake
          </MuiLink>
          , você pode usar <b>lógica de programação</b> (condicionais, loops,
          maps) para montar o layout dinamicamente."
        </Typography>
      </Paper>
      <SubSectionTitle>Componentes Exclusivos</SubSectionTitle>
      <Typography variant="body2" paragraph>
        Além dos elementos padrão do pdfMake (text, columns...), usamos
        componentes exclusivos identificados por <code>image</code>.
        <b>
          {" "}
          Os gráficos (lineChart, pieChart) são baseados na biblioteca{" "}
          <MuiLink
            href="https://www.chartjs.org/docs/latest/"
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            color="primary"
            fontWeight="bold"
          >
            Chart.js
          </MuiLink>
          .
        </b>
      </Typography>
      {/* Chart 1 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              color: "secondary.main",
            }}
          >
            <TimelineIcon />
            <Typography variant="subtitle1" fontWeight="bold">
              Gráfico de Linha (lineChart)
            </Typography>
          </Box>

          <Typography variant="caption" display="block" gutterBottom>
            Identificador: <code>"image": "lineChart"</code>
          </Typography>

          <PropertyList
            items={[
              {
                name: "dataset",
                required: true,
                desc: "Séries de dados.",
                example: "[{ label: 'Titulo', data: [1000, 2500] }]",
              },
              {
                name: "labels",
                required: true,
                desc: "Rótulos do eixo X (Tempo).",
                example: "['10:00', '10:05']",
              },
              {
                name: "isTraffic",
                desc: "Se true, converte eixo y de bits para Kbps/Mbps/Gbps.",
              },
              {
                name: "percentil",
                desc: "Linha para destacar um valor específico (ex: 95th percentil).",
                example: "{ value: 950, label: '95%', color: '#f00' }",
              },
              {
                name: "line",
                desc: "Linha contínua estática.",
                example: "{ value: 1000, label: 'Capacidade', color: 'blue' }",
              },
              {
                name: "fit",
                desc: "Largura e altura do gráfico. Padrão [500, 150].",
              },
            ]}
          />

          <CodeSnippet
            language="javascript"
            height="420px"
            code={`[{
    text: _titulo_,
    style: "header"
  },
  {
  image: "lineChart",
  dataset: [
    {
      label: "Titulo",
      alignment: "center",
      data: _graficoDados_, /// Ex: [10, 20, 30]
      borderColor: "#2469e1", // Cor da linha
      pointRadius: 0, // Tamanho dos pontos (0 = nenhum)
      fill: true // Preencher área abaixo da linha
    }
  ],
  // Formatação de data (Timestamp -> Texto)
  labels: _graficoLabels_.map((f) => dayjs.unix(f).format("DD/MM/YYYY")),
  isTraffic: true,
  percentil: {
    value: 95000000,
    label: "95th percentil",
    color: "#000"
  },
  fit: [520, 200]
}]`}
          />
        </CardContent>
      </Card>
      {/* Chart 2 */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              color: "secondary.main",
            }}
          >
            <PieChartIcon />
            <Typography variant="subtitle1" fontWeight="bold">
              Gráfico de Pizza (pieChart)
            </Typography>
          </Box>

          <Typography variant="caption" display="block" gutterBottom>
            Identificador: <code>"image": "pieChart"</code>
          </Typography>
          <PropertyList
            items={[
              {
                name: "dataset",
                required: true,
                desc: "Array de objetos contendo os dados.",
                example:
                  "[{ data: [10, 20, 30], backgroundColor: ['#red', '#blue'] }]",
              },
              {
                name: "labels",
                required: true,
                desc: "Nome (legenda) de cada fatia.",
                example: "['Baixo', 'Médio', 'Alto']",
              },
              {
                name: "fit",
                desc: "Tamanho [largura, altura]. Padrão [300, 300].",
              },
            ]}
          />

          <CodeSnippet
            language="json"
            height="240px"
            code={`[{
    text: _titulo_,
    style: "header"
  },
  {
  "image": "pieChart",
  "dataset": [
    {
      "data": _graficoDados_.slice(0, 3), // Ex: [10, 20, 30]
      "backgroundColor": ["#FB7500", "#36A2EB", "#FFCE56",] // (Opcional)
    }
  ],
  "labels": ["Baixo", "Médio", "Alto"],
  "fit": [300, 300]
}]`}
          />
        </CardContent>
      </Card>
      {/* 4. STYLES */}
      <SectionHeader number="4" icon={<CssIcon />}>
        Estilos (Style)
      </SectionHeader>
      <Typography variant="body2">
        A aba Estilo funciona como CSS. Ela já vem pré-preenchida, mas pode ser
        alterada conforme necessário.
      </Typography>
    </Box>
  );
};
